import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api } from "../lib/api";
import { useAccounts } from "../lib/account";
import { moneyFor } from "../lib/currency";
import { Link } from "react-router-dom";
import { ArrowRight, ChartLineUp, ChartLineDown } from "@phosphor-icons/react";

export default function TradesRangeDialog({ open, onOpenChange, from, to, title, subtitle }) {
  const { activeId, activeAccount } = useAccounts();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !activeId || !from || !to) return;
    setTrades([]);        // reset immediately so stale data never flashes
    setLoading(true);
    const params = new URLSearchParams({ account_id: activeId, from_date: from, to_date: to, limit: "500" });
    api.get(`/trades?${params.toString()}`)
      .then(({ data }) => setTrades(data))
      .catch(() => setTrades([]))
      .finally(() => setLoading(false));
  }, [open, activeId, from, to]);

  const wins = trades.filter(t => t.pnl > 0);
  const losses = trades.filter(t => t.pnl < 0);
  const total = trades.reduce((s, t) => s + (t.pnl || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col text-foreground">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">{subtitle || "Trade History"}</p>
              <DialogTitle className="font-display text-3xl mt-1 text-foreground">{title}</DialogTitle>
            </div>
            <div className="text-right">
              <p className={`font-mono-num text-3xl font-bold ${total >= 0 ? "text-emerald-400" : "text-red-500"}`}
                data-testid="range-total-pnl">
                {moneyFor(activeAccount, total, { sign: true })}
              </p>
              <p className="text-xs text-muted-foreground mt-1" data-testid="range-count">
                {trades.length} trade{trades.length === 1 ? "" : "s"} · {wins.length}W · {losses.length}L
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto hide-scroll">
          {loading && <p className="text-sm text-muted-foreground py-10 text-center">Loading…</p>}
          {!loading && trades.length === 0 && (
            <div className="text-sm text-muted-foreground py-16 text-center border border-dashed border-border rounded-lg mt-2"
              data-testid="range-empty">
              <p>No trades in this range.</p>
            </div>
          )}
          {!loading && trades.length > 0 && (
            <table className="w-full text-sm mt-2">
              <thead className="sticky top-0 bg-card">
                <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-muted-foreground border-b border-border">
                  <th className="py-2">Symbol</th>
                  <th className="py-2">Side</th>
                  <th className="py-2 text-right">Qty</th>
                  <th className="py-2 text-right">Entry</th>
                  <th className="py-2 text-right">Exit</th>
                  <th className="py-2 text-right">P&L</th>
                  <th className="py-2">Date</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {trades.map(t => (
                  <tr key={t.id} className="border-b border-border/60 hover:bg-secondary/40 text-foreground"
                    data-testid={`range-row-${t.id}`}>
                    <td className="py-2 font-medium text-foreground">{t.symbol}</td>
                    <td className="py-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${t.direction === "long" ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
                        {t.direction === "long"
                          ? <ChartLineUp size={10} className="inline mr-0.5" />
                          : <ChartLineDown size={10} className="inline mr-0.5" />}
                        {(t.direction || "").toUpperCase() || "—"}
                      </span>
                    </td>
                    <td className="py-2 text-right font-mono-num text-foreground">{t.quantity}</td>
                    <td className="py-2 text-right font-mono-num text-foreground">{t.entry_price}</td>
                    <td className="py-2 text-right font-mono-num text-foreground">{t.exit_price}</td>
                    <td className={`py-2 text-right font-mono-num font-bold ${t.pnl >= 0 ? "text-emerald-400" : "text-red-500"}`}>
                      {moneyFor(activeAccount, t.pnl, { sign: true })}
                    </td>
                    <td className="py-2 text-xs text-muted-foreground">{(t.entry_time || "").slice(0,10)}</td>
                    <td className="py-2 text-right">
                      <Link to={`/trades/${t.id}`} onClick={() => onOpenChange(false)}
                        className="text-primary hover:underline text-xs inline-flex items-center gap-1"
                        data-testid={`range-open-${t.id}`}>
                        Open <ArrowRight size={10} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
